import React, { useEffect, useState } from 'react';
import { string } from 'prop-types';

import { FormattedMessage } from '../../util/reactIntl';
import { getGoogleReviews } from '../../util/api';

import css from './CompanyProfilePage.module.css';

// Google "G" mark — inline (no external asset) so it satisfies the Places
// attribution requirement without a network request.
const GoogleG = () => (
  <svg className={css.googleG} viewBox="0 0 48 48" width="16" height="16" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
    />
    <path
      fill="#34A853"
      d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
    />
    <path
      fill="#FBBC05"
      d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
    />
    <path
      fill="#EA4335"
      d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
    />
  </svg>
);

/**
 * Part A — Google Reviews badge on the company profile.
 *
 * Calls our server proxy with only the profile user id; the server decides
 * whether a badge is allowed (admin-set metadata.googlePlaceId) and returns the
 * live rating or a plain reviews link. Renders nothing until resolved, nothing
 * when no badge is allowed, the rating badge when a number is available, and a
 * plain "Se våra recensioner på Google" link when Google can't be reached.
 */
const GoogleReviewsBadge = props => {
  const { profileUserId } = props;
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    if (!profileUserId) {
      return undefined;
    }
    getGoogleReviews({ userId: profileUserId })
      .then(res => {
        if (active) setData(res);
      })
      .catch(() => {
        // Never surface an error state on the profile — just show no badge.
        if (active) setData({ hasBadge: false });
      });
    return () => {
      active = false;
    };
  }, [profileUserId]);

  if (!data || !data.hasBadge || !data.reviewsUrl) {
    return null;
  }

  const hasRating = typeof data.rating === 'number';
  const ratingLabel = hasRating ? data.rating.toFixed(1).replace('.', ',') : null;

  return (
    <a
      className={css.googleBadge}
      href={data.reviewsUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      <GoogleG />
      {hasRating ? (
        <span className={css.googleBadgeText}>
          ★{' '}
          <FormattedMessage
            id="CompanyProfilePage.googleRating"
            values={{ rating: ratingLabel, count: data.reviewCount ?? 0 }}
          />
        </span>
      ) : (
        <span className={css.googleBadgeText}>
          <FormattedMessage id="CompanyProfilePage.googleReviewsFallback" />
        </span>
      )}
    </a>
  );
};

GoogleReviewsBadge.propTypes = {
  profileUserId: string,
};

export default GoogleReviewsBadge;
